import type {KeyboardEvent, ReactElement} from 'react';
import {useEffect} from 'react';
import {Button, ButtonSize, ButtonType} from '@Components/button';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {getTypeTabLabel} from '@Components/qr-code-generator/qr-code-generator.library';
import {useIsCompactQRTool, useScrollAffordances} from '@Components/qr-code-generator/qr-code-generator-hooks';
import styles from './qr-type-tabs.module.scss';
import type {QRTypeTabsProps} from './qr-type-tabs.types';
import {QR_TYPE_TAB_ICONS, QR_TYPE_RAIL_SCROLL_PX, QR_TYPE_TAB_PANEL_ID, QR_TYPE_TAB_STEP_KEYS} from './qr-type-tabs.types';

export function QRTypeTabs({types, activeType, onTypeSelect}: QRTypeTabsProps): ReactElement {
  const {scrollableRef, canScrollBack, canScrollForward, scrollBack, scrollForward} = useScrollAffordances(QR_TYPE_RAIL_SCROLL_PX);
  const isCompact = useIsCompactQRTool();

  useEffect((): void => {
    scrollableRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({block: 'nearest', inline: 'center'});
  }, [activeType, scrollableRef]);

  /**
   * A tablist owes arrow-key navigation: the tabs share one tab stop, so Tab moves past the rail and the arrows move within it. Without
   * this the roles announce a pattern the keyboard does not honour.
   */
  const onRailKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const step = QR_TYPE_TAB_STEP_KEYS[event.key];
    if (step === undefined) {
      return;
    }
    event.preventDefault();
    const nextIndex = (types.indexOf(activeType) + step + types.length) % types.length;
    onTypeSelect(types[nextIndex]);
    scrollableRef.current?.querySelectorAll<HTMLElement>('[role="tab"]')[nextIndex]?.focus();
  };

  const renderArrow = (isBack: boolean): ReactElement | null => {
    const canScroll = isBack ? canScrollBack : canScrollForward;
    if (!canScroll) {
      return null;
    }
    return (
      <Icon
        icon={isBack ? 'icon-caret-left' : 'icon-caret-right'}
        size={isCompact ? IconSize.SIZE_ICON_16 : IconSize.SIZE_ICON_20}
        isUsedAsButton
        ariaLabel={window.i18next.t(isBack ? 'pmwjs_qr_generator_previous_types' : 'pmwjs_qr_generator_next_types')}
        className={styles.qrTypeTabArrow}
        onClick={isBack ? scrollBack : scrollForward}
      />
    );
  };

  /** The fade only earns its place on the side that actually has more tabs, which is the same condition the arrow appears under. */
  const getViewportClasses = (): string => {
    return [styles.qrTypeTabRailViewport, canScrollBack ? styles.qrTypeTabRailFadeBack : '', canScrollForward ? styles.qrTypeTabRailFadeForward : ''].filter(Boolean).join(' ');
  };

  return (
    <div className={styles.qrTypeTabCarousel}>
      {renderArrow(true)}
      <div className={getViewportClasses()}>
        <div
          ref={scrollableRef}
          className={styles.qrTypeTabRail}
          role="tablist"
          aria-label={window.i18next.t('pmwjs_qr_generator_type_tabs_label')}
          onKeyDown={onRailKeyDown}
        >
          {types.map((type) => {
            return (
              <Button
                key={type}
                text={getTypeTabLabel(type)}
                icon={QR_TYPE_TAB_ICONS[type]}
                size={isCompact ? ButtonSize.SMALL : ButtonSize.MEDIUM}
                type={type === activeType ? ButtonType.SECONDARY : ButtonType.TERTIARY}
                isSelected={type === activeType}
                customClasses={styles.qrTypeTab}
                attributes={{
                  role: 'tab',
                  'aria-selected': String(type === activeType),
                  'aria-controls': QR_TYPE_TAB_PANEL_ID,
                  tabIndex: type === activeType ? '0' : '-1',
                }}
                onClick={(): void => {
                  onTypeSelect(type);
                }}
              />
            );
          })}
        </div>
      </div>
      {renderArrow(false)}
    </div>
  );
}
